<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { usePostHog } from '@/composables/usePostHog'
import { useOnboardingModalsStore, useOnboardingStore } from '@/stores/store'

const props = defineProps({
  dismissible: { type: Boolean, default: true },
})

const emit = defineEmits(['signup-click', 'dismiss'])

const STORAGE_KEY = 'sl-signup-bar-dismissed'
const visible = ref(true)
const barRef = ref(null)
const barHeight = ref(0)
let observer = null

const router = useRouter()
const { posthog } = usePostHog()
const modalsStore = useOnboardingModalsStore()
const onboardingStore = useOnboardingStore()

// The sign-up modal only lives on the Home pages; elsewhere, send the visitor
// to /#/?signup=1, which Home turns into an open modal.
function openSignup() {
  emit('signup-click')
  if (document.getElementById('onboardingModal') && modalsStore.onboardingModal) {
    posthog.capture('join_waitlist_button_clicked', { source: 'signup_bar' })
    onboardingStore.resetState()
    modalsStore.onboardingModal.show()
  } else {
    router.push({ path: '/', query: { signup: 1 } })
  }
}

function readDismissed() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function dismiss() {
  visible.value = false
  try {
    sessionStorage.setItem(STORAGE_KEY, '1')
  } catch {
    /* storage unavailable: hide for this page view only */
  }
  emit('dismiss')
}

onMounted(() => {
  if (props.dismissible && readDismissed()) visible.value = false
  // Keep a spacer the same height as the bar so it never covers the page's last content
  if (barRef.value && 'ResizeObserver' in window) {
    observer = new ResizeObserver(([entry]) => {
      barHeight.value = entry.target.offsetHeight
    })
    observer.observe(barRef.value)
  }
})

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <div v-if="visible">
    <div class="sl-bar-spacer" :style="{ height: `${barHeight + 16}px` }" aria-hidden="true" />

    <aside ref="barRef" class="sl-bar" aria-label="Sign up for SimpleLoyalty">
      <div class="sl-bar__text">
        <p class="sl-bar__title">Preview of SimpleLoyalty</p>
        <p class="sl-bar__message">
          Sign up and launch your loyalty program in 2 minutes. More than 15 marketing features.
          No credit card needed.
        </p>
      </div>

      <!-- Red panel: flat colour with a slanted edge (brand allows no gradients) -->
      <div class="sl-bar__actions">
        <button type="button" class="sl-bar__cta" @click="openSignup">Sign up for free</button>

        <button
          v-if="dismissible"
          type="button"
          class="sl-bar__close"
          aria-label="Dismiss"
          @click="dismiss"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </aside>
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cabin:wght@600;700&family=Montserrat:wght@400;500;600&display=swap');
</style>

<style scoped>
.sl-bar {
  --sl-red: #ff0000;
  --sl-black: #000000;
  --sl-white: #ffffff;

  position: fixed;
  left: 8px;
  right: 8px;
  bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  z-index: 900;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  background: var(--sl-white);
  color: var(--sl-black);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08); /* lifts the bar off white pages */
  border-radius: 8px;
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
}

.sl-bar__text {
  flex: 1;
  min-width: 0;
  align-self: center;
  padding: 14px 24px 14px 20px;
}

.sl-bar__title {
  margin: 0 0 2px;
  font-family: 'Cabin', 'Trebuchet MS', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
}

.sl-bar__message {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #404040;
}

.sl-bar__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 56px;
  background: var(--sl-red);
  clip-path: polygon(36px 0, 100% 0, 100% 100%, 0 100%); /* slanted white-to-red edge */
}

.sl-bar__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 28px;
  border: 0;
  background: var(--sl-white);
  color: var(--sl-black);
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.15s ease;
}
.sl-bar__cta:hover {
  transform: scale(1.04);
}

.sl-bar__close {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--sl-black);
  cursor: pointer;
}
.sl-bar__close:hover {
  background: rgba(0, 0, 0, 0.12);
}

.sl-bar__cta:focus-visible,
.sl-bar__close:focus-visible {
  outline: 2px solid var(--sl-black);
  outline-offset: 2px;
}

/* Mobile: white text block on top, full-width red strip with the button below */
@media (max-width: 640px) {
  .sl-bar {
    flex-direction: column;
  }
  .sl-bar__text {
    align-self: stretch;
    padding: 14px 16px 12px;
  }
  .sl-bar__title {
    font-size: 14px;
  }
  .sl-bar__message {
    font-size: 13px;
  }
  .sl-bar__actions {
    padding: 10px 10px 10px 14px;
    clip-path: none;
  }
  .sl-bar__cta {
    flex: 1;
    min-height: 44px;
    font-size: 15px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sl-bar__cta {
    transition: none;
  }
  .sl-bar__cta:hover {
    transform: none;
  }
}
</style>
